import React, { useState, useEffect } from 'react';
import { 
  FaUserCircle, FaEnvelope, FaPhone, FaLock, 
  FaSave, FaEdit, FaTimes, FaEye, FaEyeSlash 
} from 'react-icons/fa';
import { database } from '../firebase';
import { ref, onValue, update } from 'firebase/database';

const AdminProfile = () => {
  const [adminData, setAdminData] = useState({
    email: '',
    mobilenumber: '',
    role: 'Super Admin'
  });

  const [editMode, setEditMode] = useState({
    email: false,
    mobilenumber: false,
    password: false
  });

  const [tempData, setTempData] = useState({
    email: '',
    mobilenumber: '',
    currentPassword: '',     // used only for display in view mode
    newPassword: '',
    confirmPassword: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: 'info' });

  // Fetch admin data
  useEffect(() => {
    const adminRef = ref(database, 'Dr/admin');

    const unsubscribe = onValue(adminRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAdminData({
          email: data.email || '',
          mobilenumber: data.mobilenumber || '',
          role: data.role || 'Super Admin'
        });

        setTempData(prev => ({
          ...prev,
          email: data.email || '',
          mobilenumber: data.mobilenumber || '',
          currentPassword: data.password || ''  // only for display purpose
        }));
      }
      setLoading(false);
    }, (error) => {
      console.error('Firebase fetch error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const startEditing = (field) => {
    setEditMode(prev => ({ ...prev, [field]: true }));
    setMessage({ text: '', type: 'info' });

    if (field === 'password') {
      setTempData(prev => ({
        ...prev,
        newPassword: '',
        confirmPassword: ''
      }));
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setShowCurrentPassword(false); // reset view mode toggle
    }
  };

  const cancelEdit = (field) => {
    setEditMode(prev => ({ ...prev, [field]: false }));
    setMessage({ text: '', type: 'info' });

    if (field === 'password') {
      setTempData(prev => ({
        ...prev,
        newPassword: '',
        confirmPassword: ''
      }));
    }
  };

  const validatePasswordChange = () => {
    if (!tempData.newPassword) return "Please enter new password";
    if (tempData.newPassword.length < 6) return "Password must be at least 6 characters";
    if (tempData.newPassword !== tempData.confirmPassword) return "Passwords do not match";
    return "";
  };

  const handleSave = async (field) => {
    setSaving(true);
    setMessage({ text: '', type: 'info' });

    try {
      const adminRef = ref(database, 'Dr/admin');
      let updateData = {};

      if (field === 'password') {
        const error = validatePasswordChange();
        if (error) {
          setMessage({ text: error, type: 'error' });
          setSaving(false);
          return;
        }
        updateData = { password: tempData.newPassword };
      } else {
        updateData = { [field]: tempData[field] };
      }

      await update(adminRef, updateData);

      // Update displayed data (never store real password long-term)
      setAdminData(prev => ({
        ...prev,
        [field]: field === 'password' ? undefined : tempData[field]
      }));

      setEditMode(prev => ({ ...prev, [field]: false }));
      setMessage({
        text: field === 'password' 
          ? 'Password updated successfully!' 
          : `${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully!`,
        type: 'success'
      });

      if (field === 'password') {
        setTempData(prev => ({
          ...prev,
          newPassword: '',
          confirmPassword: ''
        }));
        setShowCurrentPassword(false);
      }

      setTimeout(() => setMessage({ text: '', type: 'info' }), 4500);
    } catch (error) {
      console.error('Update failed:', error);
      setMessage({ text: 'Failed to update. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setTempData(prev => ({ ...prev, [field]: value }));
    setMessage({ text: '', type: 'info' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">

          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="bg-white p-3 rounded-full shadow-lg">
                <FaUserCircle className="text-4xl text-teal-600" />
              </div>
              <div className="text-white">
                <h1 className="text-2xl font-bold">Admin Profile</h1>
                <p className="text-teal-100 mt-1">Manage your account information</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          {message.text && (
            <div className={`mx-6 mt-6 p-4 rounded-lg border ${
              message.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : message.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-gray-50 border-gray-200 text-gray-700'
            }`}>
              {message.text}
            </div>
          )}

          {/* Form Content */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Email */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <FaEnvelope className="text-teal-600" />
                  <span>Email Address</span>
                </label>
                {editMode.email ? (
                  <div className="space-y-3">
                    <input
                      type="email"
                      value={tempData.email}
                      onChange={e => handleChange('email', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleSave('email')}
                        disabled={saving}
                        className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        <FaSave /> {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => cancelEdit('email')}
                        disabled={saving}
                        className="flex-1 bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-800">{adminData.email || 'Not set'}</span>
                    <button
                      onClick={() => startEditing('email')}
                      className="p-2 text-teal-600 hover:bg-teal-50 rounded transition-colors"
                    >
                      <FaEdit />
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Number */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <FaPhone className="text-teal-600" />
                  <span>Mobile Number</span>
                </label>
                {editMode.mobilenumber ? (
                  <div className="space-y-3">
                    <input
                      type="tel"
                      value={tempData.mobilenumber}
                      onChange={e => handleChange('mobilenumber', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleSave('mobilenumber')}
                        disabled={saving}
                        className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        <FaSave /> {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => cancelEdit('mobilenumber')}
                        disabled={saving}
                        className="flex-1 bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-800">{adminData.mobilenumber || 'Not set'}</span>
                    <button
                      onClick={() => startEditing('mobilenumber')}
                      className="p-2 text-teal-600 hover:bg-teal-50 rounded transition-colors"
                    >
                      <FaEdit />
                    </button>
                  </div>
                )}
              </div>

              {/* Password - VIEW MODE with show password */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <FaLock className="text-teal-600" />
                  <span>Password</span>
                </label>

                {!editMode.password ? (
                  <div className="relative flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-800 select-none">
                      {showCurrentPassword ? tempData.currentPassword || '•••••••••••' : '•••••••••••'}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="p-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                        title={showCurrentPassword ? "Hide password" : "Show password"}
                      >
                        {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      <button
                        onClick={() => startEditing('password')}
                        className="p-2 text-teal-600 hover:bg-teal-50 rounded transition-colors"
                        title="Change password"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── EDIT MODE ───────────────────────────────────── */
                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={tempData.newPassword}
                        onChange={e => handleChange('newPassword', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 pr-10"
                        placeholder="New password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>

                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={tempData.confirmPassword}
                        onChange={e => handleChange('confirmPassword', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 pr-10"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleSave('password')}
                        disabled={saving}
                        className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        <FaSave /> {saving ? 'Saving...' : 'Update Password'}
                      </button>
                      <button
                        onClick={() => cancelEdit('password')}
                        disabled={saving}
                        className="flex-1 bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Role - Read only */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <FaUserCircle className="text-teal-600" />
                  <span>Role</span>
                </label>
                <div className="p-3 bg-teal-50 rounded-lg border border-teal-200 flex items-center justify-between">
                  <span className="text-teal-800 font-medium">{adminData.role}</span>
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">Fixed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;